import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import useUnreadChats from "@/composables/v2.1/useUnreadChats";
// @ts-ignore
import toastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import axios from "@/libs/axios";
// @ts-ignore
import i18n from "@/libs/i18n";
import { useChatsStore } from "@/store/useChatsStore";
import { ChatDto, ChatsListChat } from "@saysimple-platform/dtos";
import { ref } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";

const isArchivePending = ref(false);

export default function useArchiveChat() {
    const toast = useToast();
    const { publicApiEndpointPath } = useInboxModuleType();
    const chatsStore = useChatsStore();
    const { updateUnreadChat } = useUnreadChats();

    const archive = (chat: ChatsListChat): Promise<ChatDto> => {
        isArchivePending.value = true;

        // We like to do this directly, but it also happens on incoming websocket event. This is to visually speed things up.
        // You can see this like kind of set it now to "temporary" and the websocket will "persist" it. It won't do any backend request, so its fine.
        chatsStore.setArchive(chat.id, chat.contactId, "closed");
        updateUnreadChat({
            chatId: chat.id,
            status: "closed",
            assignedUserId: chat.assignedUserId,
            assignedTeamId: chat.assignedTeamId,
            isChatRead: Boolean(chat.read),
        });
        return axios
            .post<ChatDto>(`${publicApiEndpointPath.value}/archive`, {
                chatId: chat.id,
                channelId: chat.channelId,
            })
            .then(({ data }) => {
                // This have to be done here instead of websockets, because the websocket will check active chat equals websocket chat.
                // When archiving, you don't have an active chat anymore.
                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("entities.conversation.Conversation"),
                        text: i18n.t("entities.conversation.changed"),
                        icon: "check",
                        variant: "success",
                    },
                });

                return data;
            })
            .catch((error) => {
                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("entities.conversation.Archive"),
                        text: i18n.t("entities.conversation.could-not-archive"),
                        icon: "times",
                        variant: "error",
                    },
                });

                // Request failed, so "revert".
                chatsStore.setArchive(chat.id, chat.contactId, "open");

                return error;
            })
            .finally(() => {
                isArchivePending.value = false;
            });
    };

    const unarchive = (chat: ChatsListChat): Promise<ChatDto> => {
        isArchivePending.value = true;

        // We like to do this directly, but it also happens on incoming websocket event. This is to visually speed things up.
        // You can see this like kind of set it now to "temporary" and the websocket will "persist" it. It won't do any backend request, so its fine.
        chatsStore.setArchive(chat.id, chat.contactId, "open");
        updateUnreadChat({
            chatId: chat.id,
            status: "open",
            assignedUserId: chat.assignedUserId,
            assignedTeamId: chat.assignedTeamId,
            isChatRead: Boolean(chat.read),
        });

        return axios
            .post<ChatDto>(`${publicApiEndpointPath.value}/unarchive`, { chatId: chat.id })
            .then(({ data }) => {
                // @note done via websockets to have it for all agents at once.
                //toast({
                //    component: toastificationContent,
                //    props: {
                //        title: i18n.t("entities.conversation.Conversation"),
                //        text: i18n.t("entities.conversation.changed"),
                //        icon: "check",
                //        variant: "success",
                //    },
                //});

                return data;
            })
            .catch((error) => {
                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("entities.conversation.Unarchive"),
                        text: i18n.t("entities.conversation.could-not-unearchive"),
                        icon: "times",
                        variant: "error",
                    },
                });

                // Request failed, so "revert".
                chatsStore.setArchive(chat.id, chat.contactId, "closed");

                return error;
            })
            .finally(() => {
                isArchivePending.value = false;
            });
    };

    return {
        archive,
        unarchive,
        isArchivePending,
    };
}
